<template>
  <LoadingDiv v-if="isLoading" />

  <div class="vetting-workbench sba-slide-in" v-else>
    <template v-if="encounter">
      <div class="card z-depth-0 vetting-soft-core" :class="{ 'vetting-soft-core-add-open': adder && clusterStatus != 'v' && clusterStatus != 'r' }">
        <!-- Bio -->
        <div class="card-panel z-depth-0 grey lighten-5 vetting-soft-bio" :class="{ 'red lighten-5': encounter.status == 'd' }">
          <div class="row">
            <div class="col s12 m1 grey lighten-4 vetting-soft-count">
              <span class="chip light-blue lighten-5 center"><b>{{ curr }}/{{ total }}</b></span>
            </div>
              <br class="hide-on-med-and-up" />
              <br class="hide-on-med-and-up" />
            <div class="col s8 m7 vetting-member-copy">
              <p v-if="encounter.client_name">{{ capWords(encounter.client_name) }}</p>
              <h5>{{ capWords(encounter.name) }}</h5>
              <p class="vetting-member-id">{{ encounter.enrollee_number }}</p>

              <span v-if="encounter?.encounter_date">
                <p>
                  <span class="orange-text text-darken-4">
                    {{
                      new Date(encounter?.encounter_date).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    }}
                  </span>
                  ->
                  <span class="teal-text text-darken-4" v-if="encounter?.encounter_date_end">
                    {{
                      new Date(encounter?.encounter_date_end).toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    }}
                  </span>
                </p>
              </span>
              <span v-else>
                <p>Encounter Dates <em>not</em> set</p>
              </span>

              <p v-if="encounterAgeSex" class="blue-grey-text text-darken-2">{{ encounterAgeSex }}</p>

              <div class="vetting-review-markers" v-if="encounter.reviewMarkers?.length">
                <span
                  v-for="marker in encounter.reviewMarkers"
                  :key="`${encounter.id}-${marker.type}`"
                  class="vetting-review-marker"
                  :class="`is-${marker.type}`"
                  :title="marker.title"
                >
                  <i class="material-icons">{{ marker.icon }}</i>
                  <span>{{ marker.detail }}</span>
                </span>
              </div>

            </div>
            <div class="col s4 vetting-member-avatar">
              <img alt="" :src="encounter.profile_img" width="96" class="circle responsive-img right"
                v-if="encounter.profile_img" />
              <img alt="" :src="`https://ui-avatars.com/api/?name=${encounter.name}&size=128`" width="96"
                class="circle responsive-img right" v-else />
            </div>
          </div>

          <!-- Show flag -->
          <template v-if="encounter?.compliance_flag == 'y'">
            <div class="card-content spacer10">
              <div class="row yellow lighten-5 vetting-soft-flag">
                <div class="col s12 spacer10">
                  <label class="active orange-text darken-text-2"><i class="tiny material-icons">flag </i> Compliance
                    Flagged</label>
                  <p>Kindly review claim for re-approval.</p>
                  <br />
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Display Diagnosis -->
        <section id="display" class="vetting-soft-diagnosis" aria-label="Encounter diagnosis">
          <div class="card-content">
            <span class="vetting-diagnosis-label">Diagnosis</span>
            <h6>{{ (encounter.diagnosis || "NOT RECORDED").toUpperCase() }}</h6>
          </div>
        </section>

        <!-- Claims Display Card -->
        <div class="card vetting-soft-claims">

          <!-- Add -->
          <div class="vetting-soft-add-panel" v-if="adder && clusterStatus != 'v' && clusterStatus != 'r'">
            <AddClaimItem :turl="{ sid: forTariff?.scheme_id, pid: forTariff?.provider_id }" :reset="error" @update="handleAddClaimUpdate" />
          </div>

          <!-- Show -->
          <div class="card-content">

            <span class="card-title">Claims
              <span class="secondary-content chip clickable vetting-soft-add"
                :class="{ 'teal lighten-5 teal-text': !adder, 'orange lighten-5 orange-text': adder }"
                :title="adder ? 'close' : 'Add New Claim'"
                @click.prevent="handleAddClaim"
                v-if="clusterStatus != 'v' && clusterStatus != 'r'">
                <i class="material-icons">{{ adder ? 'close' : 'add_circle' }}</i>
              </span>
            </span>

            <!-- List claims -->
            <ol id="item-list" class="collection vetting-soft-list">
              <li class="collection-item claim-line-item" :class="{
                'has-inline-resolver': editClaim?.id === claim.id || deleteClaim?.id === claim.id,
                'is-inline-editing': editClaim?.id === claim.id,
                'is-inline-deleting': deleteClaim?.id === claim.id,
              }" v-for="claim in encounter.claims" :key="claim.id">
                <template v-if="encounter.status == 'd'">

                  <!-- Deleted Encounter Items -->
                  <span class="claim-line-copy">
                    <s>{{ capWords(claim.item) }} x {{ claim.sent_qty }} (Amount Sent: &#8358;{{
                      Number(claim.sent_price)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }})</s>
                    <b><span v-if="hasClaimComment(claim)" class="info-item tooltipped pointable" data-position="top" :data-tooltip="claim.comment">
                        <i class="material-icons grey-text">help</i>
                      </span></b>

                    <!-- Authorizations (delete) -->
                    <span v-if="claim.authorization == 'y'">
                      => <s> ({{ claim.authorization_code }}) </s>
                    </span>
                  </span>

                </template>

                <template v-else>
                  <!-- Approved Items -->
                  <span class="claim-line-copy" v-if="claim.status == 'a'">
                    <b>{{ capWords(claim.item) }}</b> x {{ claim.sent_qty }} (&#8358;{{
                      Number(claim.sent_price)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }})
                    <span class="professional-fee-badge" v-if="isProfessionalFeeClaim(claim)">Auto</span>

                    <!-- Reviewed claim -->
                    <span class="brown-text" v-if="hasClaimVettingAdjustment(claim)">
                      <b> =></b>

                      <span v-if="claim.sent_qty != claim.approved_qty"> x {{ claim.approved_qty }} </span>

                      <b> (&#8358;{{
                        Number(claim.approved_price)
                          .toFixed(2)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }})<span v-if="hasClaimComment(claim)" class="info-item tooltipped pointable" data-position="top"
                          :data-tooltip="capWords(claim.comment)">
                          <i class="material-icons grey-text">help_outline</i>
                        </span></b>
                    </span>

                    <!-- Authorizations (manual) -->
                    <span v-if="claim?.authorization_code && claim.authorization != 'y'">
                      => ({{ claim.authorization_code }})
                    </span>

                    <!-- Authorizations (active) -->
                    <span v-if="claim.authorization == 'y'">
                      => ({{ claim.authorization_code }})
                      <span class="info-item tooltipped pointable authorization-lock-inline" data-position="top"
                        :data-tooltip="claim?.slug">
                        <i class="material-icons">lock_outline</i>
                      </span>
                    </span>

                    <!-- Actions -->
                    <template v-if="clusterStatus != 'v' && clusterStatus != 'r'">
                      <span class="line-item-actions"
                        v-if="canEditClaim(claim) && editClaim?.id !== claim.id && deleteClaim?.id !== claim.id">
                        <a href="#" class="secondary-content cancel-item" @click.prevent="handleDelete(claim)"
                          v-if="canDeleteClaim(claim)"
                          title="decline">
                          <i class="material-icons" :class="{
                            'grey-text': deleteClaim != claim,
                            'red-text': deleteClaim == claim,
                          }">cancel</i>

                          </a>
                        <a href="#" class="secondary-content edit-item" @click.prevent="handleEdit(claim)"
                          title="edit">
                          <i class="material-icons" :class="{
                            'grey-text': editClaim != claim,
                            'blue-text': editClaim == claim,
                          }">edit</i>

                          </a>
                      </span>
                    </template>
                  </span>

                  <!-- Deleted Items -->
                  <span class="claim-line-copy" v-else>
                    <s>{{ capWords(claim.item) }} x {{ claim.sent_qty }} (Amount Sent: &#8358;{{
                      Number(claim.sent_price)
                        .toFixed(2)
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }})</s>
                    <b><span v-if="hasClaimComment(claim)" class="info-item tooltipped pointable" data-position="top"
                        :data-tooltip="capWords(claim.comment)">
                        <i class="material-icons grey-text">help_outline</i>
                      </span></b>

                    <!-- Authorizations (delete) -->
                    <span v-if="claim.authorization == 'y'">
                      => ({{ claim.authorization_code }})
                      <span class="info-item tooltipped pointable authorization-lock-inline" data-position="top"
                        :data-tooltip="claim.slug">
                        <i class="material-icons">lock_outline</i>
                      </span>
                    </span>

                    <!-- Actions -->
                    <template v-if="clusterStatus != 'v' && clusterStatus != 'r'">
                      <span class="line-item-actions" v-if="canResetClaim(claim)">
                        <a href="#" class="secondary-content reset-item" @click.prevent="handleReset(claim)"
                          title="reset"><i class="small material-icons grey-text">refresh</i></a>
                      </span>
                    </template>
                  </span>
                </template>

                <form class="claim-item-resolver" :id="`claim-item-resolver-${claim.id}`"
                  :class="{
                    'is-editing': editClaim?.id === claim.id,
                    'is-deleting': deleteClaim?.id === claim.id,
                    'has-error': error,
                  }"
                  @submit.prevent="deleteClaim?.id === claim.id ? handleClaimDelete() : handleClaimEdit()"
                  v-if="editClaim?.id === claim.id || deleteClaim?.id === claim.id">
                  <div class="claim-item-resolver-heading">
                    <span class="claim-item-resolver-icon">
                      <i class="material-icons">{{ editClaim?.id === claim.id ? "tune" : "delete_outline" }}</i>
                    </span>
                    <span>
                      <strong>{{ editClaim?.id === claim.id ? "Adjust claim item" : "Delete claim item" }}</strong>
                      <small>{{ capWords(claim.item) }}</small>
                    </span>
                    <button type="button" class="claim-item-resolver-dismiss" title="Close resolver"
                      aria-label="Close resolver" @click.prevent="handleRefresh">
                      <i class="material-icons">close</i>
                    </button>
                  </div>

                  <div class="authorization-review-warning" v-if="isApprovedAuthorizationClaim(claim)">
                    <i class="material-icons">warning</i>
                    <span>
                      This item was approved by <b>{{ authorizationApprovalSlug(claim) }}</b>.
                      Please review carefully before {{ deleteClaim?.id === claim.id ? "deleting" : "changing" }} this approval.
                    </span>
                  </div>

                  <template v-if="editClaim?.id === claim.id">
                    <div class="claim-item-resolver-fields is-professional-fee" v-if="isProfessionalFeeClaim(claim)">
                      <label :for="`claim-fee-qty-${claim.id}`">
                        <span>Approved quantity</span>
                        <input v-model="qty" :id="`claim-fee-qty-${claim.id}`" type="number" disabled />
                      </label>
                      <label :for="`claim-fee-amount-${claim.id}`">
                        <span>Approved amount</span>
                        <input v-model="amt" :id="`claim-fee-amount-${claim.id}`" type="number" min="0" step="0.01"
                          inputmode="decimal" />
                      </label>
                      <p>System-generated professional fee. Adjust only the approved amount; quantity and sent amount stay fixed.</p>
                    </div>

                    <div class="claim-item-resolver-fields" v-else>
                      <label :for="`claim-qty-${claim.id}`">
                        <span>Approved quantity</span>
                        <input v-model="qty" @input="handlePricing" :id="`claim-qty-${claim.id}`" type="number" />
                      </label>
                      <label :for="`claim-amount-${claim.id}`">
                        <span>Approved amount</span>
                        <input v-model="amt" :id="`claim-amount-${claim.id}`" type="number" readonly />
                      </label>
                    </div>

                    <label class="claim-item-resolver-comment" :for="`claim-edit-comment-${claim.id}`"
                      v-if="!isProfessionalFeeClaim(claim)">
                      <span>Reason for adjustment</span>
                      <input v-model="comment" :id="`claim-edit-comment-${claim.id}`" type="text"
                        placeholder="Add a reason for this adjustment"
                        :required="Number(amt) < Number(claim.sent_price)" />
                    </label>
                  </template>

                  <label class="claim-item-resolver-comment" :for="`claim-delete-comment-${claim.id}`"
                    v-if="deleteClaim?.id === claim.id">
                    <span>Reason for deleting</span>
                    <input v-model="comment" :id="`claim-delete-comment-${claim.id}`" type="text"
                      placeholder="Enter the reason for deleting this item" required />
                  </label>

                  <p class="review-limit-message" v-if="reviewLimitMessage">
                    {{ reviewLimitMessage }}
                  </p>

                  <div class="claim-item-resolver-actions">
                    <button type="button" class="claim-item-resolver-cancel" @click.prevent="handleRefresh">
                      {{ deleteClaim?.id === claim.id ? "Keep item" : "Cancel" }}
                    </button>
                    <button type="submit" class="claim-item-resolver-submit"
                      :class="{ 'is-danger': deleteClaim?.id === claim.id }">
                      <i class="material-icons">{{ editClaim?.id === claim.id ? "check" : "close" }}</i>
                      <span>{{ deleteClaim?.id === claim.id
                        ? "Delete item"
                        : (isProfessionalFeeClaim(claim) ? "Update fee" : "Apply adjustment") }}</span>
                    </button>
                  </div>
                </form>
              </li>
            </ol>

            <!-- Claims Submit -->
            <div v-if="claimsSubmitCard">
              <!-- Total -->
              <ul id="item-list" class="collection vetting-soft-total">
                <li class="collection-item">
                  <span class="right">
                    <em>(Sent: &#8358;{{ totalSent(encounter) }})</em> |

                    {{ encounter.status == "p" ? "Vetted" : "Approved" }}:
                    <b><span :class="{
                      'red-text': encounter.status == 'p',
                      'green-text text-darken-2': encounter.status != 'p',
                    }">&#8358;{{ totalApproved(encounter) }}</span></b>

                    <br />
                  </span>
                </li>
              </ul>

              <p class="review-limit-message" v-if="reviewLimitMessage">
                {{ reviewLimitMessage }}
              </p>

              <!-- Submit -->
              <template v-if="clusterStatus != 'v' && clusterStatus != 'r'">
                <div class="vetting-action-row">
                  <button type="button" class="vetting-action-btn is-secondary" @click.prevent="handleBack">
                    <i class="material-icons">arrow_back</i>
                    <span>Back</span>
                  </button>

                  <div class="vetting-action-group">
                    <button type="button" class="vetting-action-btn is-danger"
                      @click.prevent="handleEncounterForDelete(encounter)" v-if="encounter.status != 'd'">
                      <i class="material-icons">block</i>
                      <span>Reject</span>
                    </button>

                    <button type="button" class="vetting-action-btn is-warning"
                      @click.prevent="handleReVet(encounter)" v-else>
                      <i class="material-icons">refresh</i>
                      <span>Re-vet</span>
                    </button>

                    <button type="button" class="vetting-action-btn is-primary"
                      :class="{ 'is-approve': encounter.status == 'p' }" @click.prevent="handleNext('approve')">
                      <span>{{ encounter.status == "p" ? "Approve" : "Next" }}</span>
                      <i class="material-icons">{{ encounter.status == "p" ? "check_circle" : "arrow_forward" }}</i>
                    </button>
                  </div>
                </div>
              </template>

              <template v-else>
                <div class="vetting-action-row is-complete">
                  <button type="button" class="vetting-action-btn is-danger disabled" disabled>
                    <i class="material-icons">block</i>
                    <span>Reject</span>
                  </button>

                  <button type="button" class="vetting-action-btn is-primary" @click.prevent="handleBack">
                    <span>Okay</span>
                    <i class="material-icons">check_circle</i>
                  </button>
                </div>
              </template>
            </div>
          </div>

        </div>

        <!-- Edit Claims -->
        <div id="review" class="card vetting-soft-edit" :class="{ 'teal lighten-5': !error, 'red lighten-5': error }"
          v-if="claimsCard && (deleteEncounter || showLegacyExternalResolver)">

          <div class="card-content" v-if="showLegacyExternalResolver && deleteClaim">
            <form>
              <div class="authorization-review-warning" v-if="isApprovedAuthorizationClaim(deleteClaim)">
                <i class="material-icons">warning</i>
                <span>
                  This item was approved by
                  <b>{{ authorizationApprovalSlug(deleteClaim) }}</b>.
                  Please review carefully before deleting this approval.
                </span>
              </div>
              <div class="row">
                <div class="input-field col s12 m8">
                  <input v-model="item" id="item" type="text" readonly />
                  <label class="active" for="item">Item</label>
                </div>
                <div class="input-field col s6 m2">
                  <input v-model="qty" id="delete_qty" type="number" disabled />
                  <label class="active" for="delete_qty">Quantity</label>
                </div>
                <div class="input-field col s6 m2">
                  <input v-model="amt" id="amt" type="number" disabled />
                  <label class="active" for="amt">Amount</label>
                </div>
                <div class="input-field col s12" v-if="deleteHolder">
                  <input v-model="comment" id="comment" type="text" required />
                  <label class="active teal-text" for="comment">Comment</label>
                </div>
              </div>
              <div class="row">
                <div class="input-field col s12">
                  <a class="waves-effect waves-light btn col s12 m2 left vetting-soft-back-action vetting-soft-close-action" @click.prevent="handleRefresh">
                    <i class="medium material-icons left">close</i>Close
                  </a>

                  <!-- Breaker -->
                  <span class="co1 m7"></span>

                  <button class="waves-effect waves-light btn red col s12 m3 right" @click.prevent="handleClaimDelete">
                    <i class="medium material-icons left">chevron_right</i>
                    Delete Item
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div class="card-content" v-if="showLegacyExternalResolver && editClaim">
            <form>
              <div class="authorization-review-warning" v-if="isApprovedAuthorizationClaim(editClaim)">
                <i class="material-icons">warning</i>
                <span>
                  This item was approved by
                  <b>{{ authorizationApprovalSlug(editClaim) }}</b>.
                  Please review carefully before changing this approval.
                </span>
              </div>
              <div class="professional-fee-edit-panel" v-if="isProfessionalFeeClaim(editClaim)">
                <div class="row">
                  <div class="input-field col s12 m7">
                    <input v-model="item" id="professional_fee_item" type="text" readonly />
                    <label class="active" for="professional_fee_item">Item</label>
                  </div>
                  <div class="input-field col s6 m2">
                    <input v-model="qty" id="professional_fee_qty" type="number" disabled />
                    <label class="active" for="professional_fee_qty">Quantity</label>
                  </div>
                  <div class="input-field col s6 m3 professional-fee-amount-field">
                    <input v-model="amt" id="professional_fee_amt" type="number" min="0" step="0.01" inputmode="decimal" />
                    <label class="active teal-text" for="professional_fee_amt">Approved Amount</label>
                  </div>
                  <div class="col s12">
                    <p class="professional-fee-edit-note">
                      System-generated professional fee. Adjust only the approved amount; quantity and sent amount stay fixed.
                    </p>
                  </div>
                </div>
              </div>
              <div class="row" v-else-if="editHolder">
                <div class="input-field col s12">
                  <input v-model="comment" id="comment" type="text" />
                  <label class="active teal-text" for="comment">Comment</label>
                </div>
              </div>
              <div class="row" v-else>
                <div class="input-field col s12 m8">
                  <input v-model="item" id="item" type="text" readonly />
                  <label class="active" for="qty">Item</label>
                </div>
                <div class="input-field col s6 m2 teal lighten-5">
                  <input v-model="qty" @input="handlePricing" id="qty" type="number" />
                  <label class="active teal-text" for="qty">Quantity</label>
                </div>
                <div class="input-field col s6 m2">
                  <input v-model="amt" id="amt" type="number" readonly />
                  <label class="active" for="amt">Amount (Approval)</label>
                </div>
              </div>
              <p class="review-limit-message" v-if="reviewLimitMessage">
                {{ reviewLimitMessage }}
              </p>
              <div class="row">
                <div class="input-field col s12">
                  <a class="waves-effect waves-light btn col s12 m2 left vetting-soft-back-action vetting-soft-close-action" @click.prevent="handleRefresh">
                    <i class="medium material-icons left">close</i>Close
                  </a>

                <!-- Breaker -->
                <span class="co1 m7"></span>

                  <button class="waves-effect waves-light btn blue darken-2 col s12 m3 right" @click.prevent="handleClaimEdit">
                    <i class="medium material-icons left">chevron_right</i>
                    {{ isProfessionalFeeClaim(editClaim) ? "Update Fee" : "Edit Item" }}
                  </button>
                </div>
              </div>
            </form>
          </div>

          <div class="card-content" v-if="deleteEncounter">
            <form>
              <div class="row">
                <div class="input-field col s12">
                  <input v-model="comment" id="comment" type="text" />
                  <label class="active teal-text" for="comment">Comment (Rejection Reason)</label>
                </div>
              </div>
              <div class="row">
                <div class="input-field col s12">
                  <a class="waves-effect waves-light btn black lighten-2 col s12 m2 left" @click.prevent="handleRefresh">
                    <i class="medium material-icons left">chevron_left</i>Back
                  </a>

                  <!-- Breaker -->
                  <span class="col m7"></span>

                  <button class="waves-effect waves-light btn red col s12 m3 right" @click.prevent="handleEncounterDelete">
                    <i class="medium material-icons left">highlight_off</i>
                    Confirm Reject
                  </button>
                </div>
              </div>
            </form>
          </div>

        </div>

      </div>
    </template>

    <div class="section center" v-if="!encounter">
      <h6>No Claims Encounter <em>found</em>...</h6>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, watchEffect, nextTick } from "vue";
import { useStore } from "vuex";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import AddClaimItem from "@/components/claim/AddClaimItemComponent.vue";
import { ageSex, capitalizeFirstLetter, capWordsFx, } from "@/composables/fxLibrary/coreFx.js";
import {
  totalSentFx,
  totalApprovedFx,
} from "@/composables/calFunction/cal";
import nativeUi from "@/ui/native-ui";

export default {
  name: "VetClaimComponent",
  components: { LoadingDiv, AddClaimItem },
  props: ["forTariff", "clusterStatus", "cluster", "encounterId"],
  emits: ["vetted", "selected"],
  setup(props, context) {
    const store = useStore();
    const response = ref(null);
    watch(() => response.value?.id, (id) => {
      if (id) context.emit("selected", id);
    }, { flush: "sync" });
    const encounterAgeSex = computed(() => {
      const record = response.value;
      if (!record) return null;

      const birthDate = record.dob ? new Date(record.dob) : null;
      const dob = birthDate && Number.isFinite(birthDate.getTime()) && birthDate <= new Date()
        ? record.dob
        : null;
      const sex = typeof record.sex === "string" ? record.sex.trim() : null;
      return ageSex(dob, sex).fx.value;
    });
    const responseAll = ref([]);
    const isLoading = ref(true);
    const error = ref(false);
    const curr = ref(0);
    const total = ref(0);
    const deleteClaim = ref(null);
    const deleteHolder = ref(false);
    const editClaim = ref(null);
    const editHolder = ref(false);
    const deleteEncounter = ref(null);
    const claimsCard = ref(false);
    const showLegacyExternalResolver = false;
    const claimsDisplayCard = ref(true);
    const claimsSubmitCard = ref(true);
    const claimsList = ref([]);
    const item = ref(null);
    const qty = ref(null);
    const amt = ref(null);
    const standard = ref(null);
    const comment = ref(null);
    const adder = ref(null);
    const professionalFeeManualOverride = ref(false);
    const reviewLimitMessage = ref(null);
    const touchedClaimIds = new Set();

    const claimStores = (claim) => {
      if (!claim?.stores) {
        return {};
      }

      if (typeof claim.stores === "string") {
        try {
          return JSON.parse(claim.stores) || {};
        } catch {
          return {};
        }
      }

      return claim.stores;
    };

    const hasClaimComment = (claim) => String(claim?.comment || "").trim().length > 0;

    const isMissingApprovalValue = (value) => value === null || value === undefined || value === "";

    const isLegacyOneProviderPlaceholder = (claim) => {
      const stores = claimStores(claim);
      const approvedQty = Number(claim?.approved_qty);
      const approvedPrice = Number(claim?.approved_price);
      const sentQty = Number(claim?.sent_qty);
      const sentPrice = Number(claim?.sent_price);

      return Boolean(stores?.oneprovider)
        && claim?.authorization === "n"
        && claim?.status === "a"
        && Number.isFinite(approvedQty)
        && Number.isFinite(approvedPrice)
        && approvedQty === 0
        && approvedPrice === 0
        && (sentQty > 0 || sentPrice > 0)
        && !hasClaimComment(claim);
    };

    const claimApprovalNeedsInitialization = (claim) => {
      return isMissingApprovalValue(claim?.approved_qty)
        || isMissingApprovalValue(claim?.approved_price)
        || isLegacyOneProviderPlaceholder(claim);
    };

    const claimNumbersMatch = (left, right) => {
      const leftNumber = Number(left);
      const rightNumber = Number(right);

      return Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
        ? leftNumber === rightNumber
        : left === right;
    };

    const hasClaimVettingAdjustment = (claim) => {
      if (claimApprovalNeedsInitialization(claim)) {
        return false;
      }

      return !claimNumbersMatch(claim?.sent_qty, claim?.approved_qty)
        || !claimNumbersMatch(claim?.sent_price, claim?.approved_price);
    };

    const initializeClaimApproval = (claim) => {
      const sentQty = Number(claim.sent_qty);
      const sentPrice = Number(claim.sent_price);
      const standardPrice = Number(claim.standard_price);
      const standardTotal = Number.isFinite(standardPrice) && standardPrice > 0
        ? sentQty * standardPrice
        : null;

      claim.approved_qty = claim.sent_qty;

      if (standardTotal !== null && sentPrice > standardTotal) {
        claim.approved_price = standardTotal;
        claim.comment = "Over Billing";
      } else {
        claim.approved_price = claim.sent_price;
      }
    };

    const parseMoney = (value) => {
      const parsed = Number(String(value ?? 0).replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const isProfessionalFeeClaim = (claim) => {
      const stores = claimStores(claim);
      return (
        stores?.system_generated === "professional_fee" ||
        claim?.system_generated === "professional_fee" ||
        String(claim?.item || "").trim().toLowerCase() === "professional fee"
      );
    };

    const currentUserSlug = () => {
      return String(store.state.user?.slug || "").trim();
    };

    const isClosedCluster = () => {
      return props.clusterStatus === "v" || props.clusterStatus === "r";
    };

    const isDeclinedAuthorizationClaim = (claim) => {
      return claim?.authorization === "z" && claim?.status === "d";
    };

    const isApprovedAuthorizationClaim = (claim) => {
      return claim?.authorization === "y";
    };

    const authorizationApprovalSlug = (claim) => {
      const original = authOriginalDecision(claim);
      const slug = String(original?.slug || claim?.slug || "").trim();

      return slug || "the authorization team";
    };

    const canMutateClaim = (claim) => {
      return !!claim && !isClosedCluster() && response.value?.status !== "d" && !isDeclinedAuthorizationClaim(claim);
    };

    const canEditClaim = (claim) => {
      return canMutateClaim(claim);
    };

    const canDeleteClaim = (claim) => {
      return canMutateClaim(claim) && !isProfessionalFeeClaim(claim);
    };

    const canResetClaim = (claim) => {
      return (
        !!claim &&
        !isClosedCluster() &&
        response.value?.status !== "d" &&
        claim.status === "d" &&
        claim.authorization !== "y" &&
        !isProfessionalFeeClaim(claim)
      );
    };

    const parseFiniteNumber = (value) => {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      const parsed = Number(String(value).replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    };

    const authDecisionSnapshot = (claim) => {
      return {
        authorization: claim?.authorization,
        status: claim?.status,
        authorization_code: claim?.authorization_code,
        approved_qty: claim?.approved_qty,
        approved_price: claim?.approved_price,
        comment: claim?.comment,
        slug: claim?.slug,
      };
    };

    const authOriginalDecision = (claim) => {
      const stores = claimStores(claim);
      if (stores?.auth_original) {
        return stores.auth_original;
      }

      const snapshot = authDecisionSnapshot(claim);

      return {
        authorization: stores?.authorization ?? snapshot.authorization,
        status: stores?.status ?? (snapshot.authorization === "y" ? "a" : snapshot.status),
        authorization_code: stores?.authorization_code ?? snapshot.authorization_code,
        approved_qty: stores?.approved_qty ?? snapshot.approved_qty,
        approved_price: stores?.approved_price ?? snapshot.approved_price,
        comment: stores?.comment ?? snapshot.comment,
        slug: stores?.slug ?? snapshot.slug,
      };
    };

    const claimStoresWithAuthDecision = (claim, action) => {
      const stores = { ...claimStores(claim) };
      const authOriginal = stores.auth_original || authOriginalDecision(claim);
      const slug = currentUserSlug();

      stores.auth_original = authOriginal;
      stores.authorization = stores.authorization ?? authOriginal.authorization;
      stores.status = stores.status ?? authOriginal.status;
      stores.authorization_code = stores.authorization_code ?? authOriginal.authorization_code;
      stores.approved_qty = stores.approved_qty ?? authOriginal.approved_qty;
      stores.approved_price = stores.approved_price ?? authOriginal.approved_price;
      stores.comment = stores.comment ?? authOriginal.comment;
      stores.slug = stores.slug ?? authOriginal.slug;
      stores.overturned_slug = slug;
      stores.claim_overturn = {
        ...(stores.claim_overturn || {}),
        action,
        by_slug: slug,
      };

      return stores;
    };

    const claimStoresWithReviewDeletion = (claim, action) => {
      const stores = { ...claimStores(claim) };
      const existing = stores.claim_review_deletion || {};
      const hasStoredValue = (key) => Object.prototype.hasOwnProperty.call(stores, key);
      const snapshot = existing.snapshot || {
        authorization: "z",
        status: "a",
        approved_qty: hasStoredValue("approved_qty") ? stores.approved_qty : claim.approved_qty,
        approved_price: hasStoredValue("approved_price") ? stores.approved_price : claim.approved_price,
        comment: hasStoredValue("comment") ? stores.comment : claim.comment,
      };

      stores.claim_review_deletion = {
        origin: "claim_review",
        action,
        snapshot,
        by_slug: currentUserSlug(),
      };

      return stores;
    };

    const authorizedClaimCeiling = (claim) => {
      if (claim?.authorization !== "y") {
        return null;
      }

      const original = authOriginalDecision(claim);
      const originalQty = parseFiniteNumber(original?.approved_qty);
      const originalPrice = parseFiniteNumber(original?.approved_price);
      const sentQty = parseFiniteNumber(claim?.sent_qty);
      const sentPrice = parseFiniteNumber(claim?.sent_price);

      return {
        qty: originalQty !== null && sentQty !== null ? Math.min(originalQty, sentQty) : originalQty ?? sentQty,
        price: originalPrice !== null && sentPrice !== null ? Math.min(originalPrice, sentPrice) : originalPrice ?? sentPrice,
      };
    };

    const exceedsAuthorizedClaimCeiling = (claim, nextQty, nextPrice) => {
      if (claim?.authorization !== "y" || claim?.status !== "a") {
        return false;
      }

      const ceiling = authorizedClaimCeiling(claim);
      if (!ceiling) {
        return false;
      }

      const qtyValue = parseFiniteNumber(nextQty);
      const priceValue = parseFiniteNumber(nextPrice);

      return (
        (ceiling.qty !== null && qtyValue !== null && qtyValue > ceiling.qty) ||
        (ceiling.price !== null && priceValue !== null && priceValue > ceiling.price)
      );
    };

    const markClaimTouched = (claim) => {
      if (claim?.id !== undefined && claim?.id !== null) {
        touchedClaimIds.add(claim.id);
      }
    };

    const hasClaimSlug = (claim) => {
      return String(claim?.slug || "").trim();
    };

    const claimWithReviewerSlug = (claim) => {
      const slug = currentUserSlug();
      if (!slug) {
        return claim;
      }

      if (touchedClaimIds.has(claim?.id) || !hasClaimSlug(claim)) {
        return { ...claim, slug };
      }

      return claim;
    };

    const claimsForBulkSubmission = (claims = []) => {
      return claims.map((claim) => claimWithReviewerSlug(claim));
    };

    const cloneClaimForAction = (claim) => {
      return {
        ...claim,
        stores: claimStores(claim),
      };
    };

    const cloneEncounterForAction = (selectedEncounter) => {
      return {
        ...selectedEncounter,
        claims: (selectedEncounter?.claims || []).map(cloneClaimForAction),
      };
    };

    const activeBaseClaims = () => {
      return (response.value?.claims || []).filter((claim) => {
        return claim?.status !== "d" && !isProfessionalFeeClaim(claim);
      });
    };

    const professionalFeeClaim = () => {
      return (response.value?.claims || []).find((claim) => {
        return claim?.status !== "d" && isProfessionalFeeClaim(claim);
      });
    };

    const normalApprovedBaseTotal = () => {
      return activeBaseClaims().reduce((sum, claim) => {
        return sum + parseMoney(claim?.approved_price);
      }, 0);
    };

    const normalSentBaseTotal = () => {
      return activeBaseClaims().reduce((sum, claim) => {
        return sum + parseMoney(claim?.sent_price);
      }, 0);
    };

    const professionalFeePercentage = (claim = professionalFeeClaim()) => {
      const stores = claimStores(claim);
      const configured = parseMoney(
        stores?.professional_fee_percentage ??
        props.forTariff?.scheme?.professional_fee_percentage ??
        props.forTariff?.professional_fee_percentage
      );

      if (configured > 0) {
        return configured;
      }

      const sentBase = normalSentBaseTotal();
      const sentFee = parseMoney(claim?.sent_price);

      return sentBase > 0 && sentFee > 0 ? (sentFee / sentBase) * 100 : 0;
    };

    const syncProfessionalFeeApproval = ({ force = false } = {}) => {
      const feeClaim = professionalFeeClaim();
      if (!feeClaim) {
        return;
      }

      feeClaim.approved_qty = 1;

      if (professionalFeeManualOverride.value && !force) {
        return;
      }

      const percentage = professionalFeePercentage(feeClaim);
      if (percentage <= 0) {
        return;
      }

      const approvedBase = normalApprovedBaseTotal();
      const approvedFee = approvedBase > 0 ? Number(((approvedBase * percentage) / 100).toFixed(2)) : 0;

      feeClaim.approved_price = approvedFee;
      feeClaim.status = "a";
      feeClaim.comment =
        approvedFee === parseMoney(feeClaim.sent_price)
          ? null
          : approvedFee === 0
            ? "Professional fee set to zero because all other approved claims are zero."
            : "Professional fee adjusted after claim vetting.";
    };

    const syncProfessionalFeeAfterBaseChange = () => {
      professionalFeeManualOverride.value = false;
      syncProfessionalFeeApproval({ force: true });
    };

    // toolTip
    const toolTip = () => {
      var elems = document.querySelectorAll(".tooltipped");
      nativeUi.Tooltip.init(elems, {});
    };

    // restart toolTip
    const restartComments = () => {
      // load comment
      setTimeout(() => {
        toolTip();
      }, 2000);
    };

    // load after data loads and updates
    watchEffect(() => {
      if (response.value) {
        restartComments();
      }
    });

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // total calculations (sent)
    const totalSent = (encounter) => {
      return totalSentFx(encounter);
    };

    // total calculations (approved)
    const totalApproved = (encounter) => {
      return totalApprovedFx(encounter);
    };

    // next Unvetted
    onMounted(() => {
      if (props.cluster) {
        // start
        responseAll.value = props.cluster;
        // total
        total.value = responseAll.value.length;
        // next
        updateClusterData(responseAll.value);
      }
    });

    // starter
    const updateClusterData = (val) => {
      if (val) {
        // check if available for vetting
        val.forEach((x, y) => {
          if (x.id == props.encounterId) {
            // (seen) good to go
            response.value = x;
            professionalFeeManualOverride.value = false;
            // number
            curr.value = y + 1;
          }
        });
        if (!response.value) {
          // next
          handleNext("next");
        }
      }
      // end load
      isLoading.value = false;
    };

    // clear inputs
    const claimInputClear = () => {
      // hide show cards
      claimsCard.value = false;
      claimsSubmitCard.value = true;

      // clear all
      item.value = null;
      qty.value = null;
      amt.value = null;
      standard.value = null;
      comment.value = null;
      reviewLimitMessage.value = null;

      // clear delete (if needed)
      deleteClaim.value = null;
      // clear editHolder
      deleteHolder.value = false;
      // clear edit (if needed)
      editClaim.value = null;
      // clear editHolder
      editHolder.value = false;
      // clear encounter delete
      deleteEncounter.value = null;
      // clear error (if needed)
      error.value = false;

      // retart Comments
      restartComments();
    };

    const scrollToLocalSection = async (id) => {
      await nextTick();
      window.requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (!target) {
          return;
        }

        const top = target.getBoundingClientRect().top + window.scrollY - 18;
        window.scrollTo({ behavior: "smooth", top: Math.max(top, 0) });
      });
    };

    const revealClaimResolver = async (claimId) => {
      await nextTick();

      window.requestAnimationFrame(() => {
        const resolver = document.getElementById(`claim-item-resolver-${claimId}`);
        if (!resolver) {
          return;
        }

        const viewportGap = 24;
        const overflow = resolver.getBoundingClientRect().bottom - (window.innerHeight - viewportGap);

        if (overflow > 0) {
          window.scrollBy({
            top: overflow,
            behavior: "smooth",
          });
        }
      });
    };

    // add new claim
    const handleAddClaim = () => {
      if (adder.value) {
        adder.value = false;
      } else {
        adder.value = true;
      }
      scrollToLocalSection("display");
    }

    // add only approved
    // sent set to zero
    const handleAddClaimUpdate = async (val) => {

      if (val?.id === 'clearClaim') {
          // recall handleClaim
          handleAddClaim();
      } 

      if (val?.id === 'addNewClaim') {

        // clear all errors first
        error.value = false;

        // update
        let newVal = {
          encounter_id: response.value.id,
          item: val.value.claim,
          sent_qty: 0,
          sent_price: 0,
          approved_qty: val.value.qty,
          approved_price: val.value.amt,
          standard_price: val.value.standard,
          comment: "New Item (added by HMO)",
          status: "a",
        };

        const slug = currentUserSlug();
        if (slug) {
          newVal.slug = slug;
        }

        // post update
        const { res, err } = await postItem("claim", newVal);

        if (res.value) {
          // success
          response.value.claims.push({
            ...res.value.data,
          });

          syncProfessionalFeeAfterBaseChange();

          //reset comments manually
          restartComments();

          // clear
          handleAddClaim();

        } else {
          error.value = err.value;
        }


      }


    }

    // edit claim
    const handleEdit = async (claim) => {
      if (!canEditClaim(claim)) {
        return;
      }

      if (claim != editClaim.value) {
        // clear all
        claimInputClear();

        // hide show cards
        claimsCard.value = true;
        claimsSubmitCard.value = false;

        // for main
        editClaim.value = claim;
        item.value = claim.item;
        qty.value = claim.approved_qty;
        amt.value = claim.approved_price;
        standard.value = claim.standard_price;
        comment.value = claim.comment || null;
        editHolder.value = true;
        await revealClaimResolver(claim.id);
      } else {
        // same item was clicked twice
        // clear all
        claimInputClear();
      }
    };

    // delete claim
    const handleDelete = async (claim) => {
      if (!canDeleteClaim(claim)) {
        return;
      }

      if (claim != deleteClaim.value) {
        // clear all
        claimInputClear();

        // hide show cards
        claimsCard.value = true;
        claimsSubmitCard.value = false;

        // for main
        deleteClaim.value = claim;
        item.value = claim.item;
        qty.value = claim.sent_qty;
        amt.value = claim.sent_price;
        standard.value = claim.standard_price;
        comment.value = null;
        deleteHolder.value = true;
        await revealClaimResolver(claim.id);
      } else {
        // same item was clicked twice
        // clear all
        claimInputClear();
      }
    };

    // prepare delete encounter
    const handleEncounterForDelete = (encounter) => {
      // clear all
      claimInputClear();

      // hide show cards
      claimsCard.value = true;
      claimsSubmitCard.value = false;

      // for main
      deleteEncounter.value = encounter;
      scrollToLocalSection("review");
    };

    // re-vet
    const handleReVet = (encounter) => {
      // clear all
      claimInputClear();

      // restore
      if (response.value == encounter) {
        response.value.claims.forEach((x) => {
          if (isDeclinedAuthorizationClaim(x)) {
            return;
          }

          // all
          x.status = "a";
          x.approved_qty = null; // insurance (should be null already) // encounter computed will fix temp
          x.approved_price = null; // insurance (should be null already) // encounter computed will fix temp
          x.comment = null;

          // update authorization y,z with stores
          if (x.authorization !== "n") {
            const stores = x.authorization === "y" ? authOriginalDecision(x) : claimStores(x);
            x.approved_qty = stores?.approved_qty;
            x.approved_price = stores?.approved_price;
            x.comment = stores?.comment;
          }

          markClaimTouched(x);
        });
        response.value.status = "p"; // switch back to pending
        syncProfessionalFeeAfterBaseChange();
      }
    };

    // reset claim
    const handleReset = (claim) => {
      if (!canResetClaim(claim)) {
        return;
      }

      // edit
      const index = response.value.claims.findIndex((x) => x.id === claim.id);
      if (index !== -1) {
        const claimForReset = response.value.claims[index];
        const stores = claimForReset.authorization === "y" ? authOriginalDecision(claimForReset) : claimStores(claimForReset);
        const reviewDeletionStores = claimForReset.authorization === "z"
          ? claimStoresWithReviewDeletion(claimForReset, "restore")
          : stores;
        const reviewDeletionSnapshot = reviewDeletionStores?.claim_review_deletion?.snapshot;
        const restoredAuthorizationValues = claimForReset.authorization !== "n"
          ? {
            authorization: reviewDeletionSnapshot?.authorization ?? stores?.authorization ?? claimForReset.authorization,
            authorization_code: stores?.authorization_code ?? claimForReset.authorization_code,
            approved_qty: reviewDeletionSnapshot?.approved_qty ?? stores?.approved_qty ?? claimForReset.approved_qty,
            approved_price: reviewDeletionSnapshot?.approved_price ?? stores?.approved_price ?? claimForReset.approved_price,
            comment: reviewDeletionSnapshot && Object.prototype.hasOwnProperty.call(reviewDeletionSnapshot, "comment")
              ? reviewDeletionSnapshot.comment
              : (stores?.comment ?? claimForReset.comment),
            stores: claimForReset.authorization === "y"
              ? claimStoresWithAuthDecision(claimForReset, "reset")
              : reviewDeletionStores,
          }
          : {};

        // claim status = a = active
        response.value.claims[index] = {
          ...response.value.claims[index],
          ...restoredAuthorizationValues,
          status: "a",
        };
        markClaimTouched(response.value.claims[index]);
        // encounter status = p = pending vetting
        response.value.status = "p";

        // clear entry
        claimInputClear();
        syncProfessionalFeeAfterBaseChange();
      }
    };

    // load claims
    const handleClaimDelete = () => {
      if (!canDeleteClaim(deleteClaim.value)) {
        claimInputClear();
        return;
      }

      if (deleteHolder.value && comment.value) {
        // edit
        const index = response.value.claims.findIndex(
          (x) => x.id === deleteClaim.value.id
        );
        if (index !== -1) {
          const claimForDelete = response.value.claims[index];
          const storedAuthorizationValues = claimForDelete.authorization === "y"
            ? {
              stores: claimStoresWithAuthDecision(claimForDelete, "delete"),
            }
            : claimForDelete.authorization !== "n"
            ? {
              stores: claimStoresWithReviewDeletion(claimForDelete, "delete"),
            }
            : {};

          // claim status = d = delete
          response.value.claims[index] = {
            ...claimForDelete,
            ...storedAuthorizationValues,
            approved_qty: null,
            approved_price: null,
            comment: comment.value,
            status: "d",
          };
          markClaimTouched(response.value.claims[index]);
          // encounter status = p = pending vetting
          response.value.status = "p";

          // clear entry
          claimInputClear();
          syncProfessionalFeeAfterBaseChange();
        }
      } else {
        // input default
        comment.value = "Not applicable";
        deleteHolder.value = true;

        // reset error (if required)
        error.value = false;
      }
    };

    // load claims
    const handleClaimEdit = () => {
      if (!canEditClaim(editClaim.value)) {
        claimInputClear();
        return;
      }

      if (isProfessionalFeeClaim(editClaim.value)) {
        if (item.value && amt.value !== null && amt.value !== "" && Number(amt.value) >= 0) {
          reviewLimitMessage.value = null;

          if (exceedsAuthorizedClaimCeiling(editClaim.value, 1, amt.value)) {
            reviewLimitMessage.value = "Claims review cannot approve above the authorization decision.";
            error.value = true;
            return;
          }

          const index = response.value.claims.findIndex(
            (x) => x.id === editClaim.value.id
          );

          if (index !== -1) {
            const approvedPrice = Number(Number(amt.value).toFixed(2));
            const authStoreUpdate = response.value.claims[index].authorization === "y"
              ? { stores: claimStoresWithAuthDecision(response.value.claims[index], "edit") }
              : {};
            response.value.claims[index] = {
              ...response.value.claims[index],
              ...authStoreUpdate,
              approved_qty: 1,
              approved_price: approvedPrice,
              comment:
                approvedPrice === parseMoney(response.value.claims[index].sent_price)
                  ? null
                  : "Professional fee manually vetted.",
              status: "a",
            };
            markClaimTouched(response.value.claims[index]);
            response.value.status = "p";
            professionalFeeManualOverride.value = true;
          }

          claimInputClear();
          error.value = false;
        } else {
          error.value = true;
        }

        return;
      }

      // start action
      if (item.value && qty.value && amt.value) {
        reviewLimitMessage.value = null;

        if (exceedsAuthorizedClaimCeiling(editClaim.value, qty.value, amt.value)) {
          reviewLimitMessage.value = "Claims review cannot approve above the authorization decision.";
          error.value = true;
          return;
        }

        if (
          (editHolder.value && comment.value) ||
          Number(amt.value) >= Number(editClaim.value.sent_price) // proceed to reload approved amounts if new amount exceeds sent amount // sent amount will be validated below with standard and qty
        ) {
          // format for saving
          const { fx } = capitalizeFirstLetter(item.value);

          if (editClaim.value) {
            // edit
            const index = response.value.claims.findIndex(
              (x) => x.id === editClaim.value.id
            );
            if (index !== -1) {
              const authStoreUpdate = response.value.claims[index].authorization === "y"
                ? { stores: claimStoresWithAuthDecision(response.value.claims[index], "edit") }
                : {};
              // found record
              // check if amount is greater 
              // base price or sent price - whichever is smaller
              let baseAmount = response.value.claims[index].sent_qty * response.value.claims[index].standard_price;
              if (response.value.claims[index].sent_price < baseAmount) { baseAmount = response.value.claims[index].sent_price }
              // continue logic
              if (
                Number(amt.value) >
                Number(baseAmount)
              ) {
                // reload approved price & qty
                response.value.claims[index] = {
                  ...response.value.claims[index],
                  ...authStoreUpdate,
                  item: fx.value,
                  approved_qty: response.value.claims[index].approved_qty,
                  approved_price: response.value.claims[index].approved_price,
                  comment: null,
                };
              } else {
                // load vetted price & qty
                response.value.claims[index] = {
                  ...response.value.claims[index],
                  ...authStoreUpdate,
                  item: fx.value,
                  approved_qty: qty.value,
                  approved_price: amt.value,
                  comment: comment.value,
                };
              }
              markClaimTouched(response.value.claims[index]);
              // encounter status = p = pending vetting
              response.value.status = "p";
              syncProfessionalFeeAfterBaseChange();
            }
          }

          // clear entry
          claimInputClear();

          // reset error (if required)
          error.value = false;
        } else {
          // input default
          if (editClaim.value.comment) {
            comment.value = editClaim.value.comment;
          } else {
            comment.value = "Over Prescription";
          }
          editHolder.value = true;
          revealClaimResolver(editClaim.value.id);

          // reset error (if required)
          error.value = false;
        }
      } else {
        error.value = true;
      }
    };

    // reject encounter
    const handleEncounterDelete = () => {
      if (deleteEncounter.value && comment.value) {

        // call handle next
        handleNext("reject");

      } else {
        // set error
        error.value = true;
      }
    };

    // watch qty & price
    const handlePricing = () => {
      amt.value = (Number(qty.value) * Number(standard.value)).toFixed(2);
    };

    // refresh field
    const handleRefresh = () => {
      // clear entry
      claimInputClear();
    };

    // handle back
    const handleBack = () => {
      // done
      context.emit("vetted", {
        status: "back",
      });
    };

    // handle approve
    const handleNext = async (val) => {
      // starter
      isLoading.value = true;
      error.value = false;
      reviewLimitMessage.value = null;

      // approve workflow
      if (val == "approve") {
        syncProfessionalFeeApproval();

        // start
        let newVal = {
          encounter: { id: response.value.id, status: "v" },
          claims: claimsForBulkSubmission(response.value.claims),
        };

        // post update
        const { res, err } = await postItem("claim/update/bulk", newVal);

        if (res.value) {
          // mark
          response.value.status = "v";
        } else {
          error.value = err.value;
          reviewLimitMessage.value = err.value || "Unable to complete claim review.";
          isLoading.value = false;
          return;
        }
      }

      // reject workflow
      if (val == "reject") {
        // response copy
        let responseCopy = cloneEncounterForAction(response.value);

        // decline responseCopy
        responseCopy.status = "d";

        // decline workflow
        responseCopy.claims.forEach((x) => {
          if (isDeclinedAuthorizationClaim(x)) {
            return;
          }

          // keep authorization z and y in stores (for reference)
          if (x.authorization === "y") {
            x.stores = claimStoresWithAuthDecision(x, "reject");
          } else if (x.authorization !== "n") {
            const stores = claimStores(x);
            stores.approved_qty = x.approved_qty;
            stores.approved_price = x.approved_price;
            stores.comment = x.comment;
            // reload stores
            x.stores = stores;
          }
          // wholesale update
          x.approved_qty = null;
          x.approved_price = null;
          x.comment = comment.value;
          x.status = "d";
          markClaimTouched(x);
        });

        // start
        let newVal = {
          encounter: { id: responseCopy.id, status: responseCopy.status },
          claims: claimsForBulkSubmission(responseCopy.claims),
        };

        // post update
        const { res, err } = await postItem("claim/update/bulk", newVal);

        if (res.value) {
          // mark
          response.value = responseCopy;
        } else {
          error.value = err.value;
          reviewLimitMessage.value = err.value || "Unable to complete claim review.";
          isLoading.value = false;
          return;
        }

      }

      // update
      if (val != "next") {
        // next is from onMounted!
        responseAll.value.forEach((x) => {
          if (x.id == response.value.id) {
            x = response.value; // update
          }
        });

        // clear regardless
        response.value = null;
      }

      // NEXT
      // CLAIM
      // SELECTED 
      // HERE
      responseAll.value.forEach((x, y) => {
        if (x.status === "p" && response.value?.status !== "p") {
          // good to go
          response.value = x;
          professionalFeeManualOverride.value = false;
          // number
          curr.value = y + 1;
        }
      });

      // route
      if (!response.value) {
        // done
        context.emit("vetted", {
          status: "success",
          value: responseAll.value,
        });
      }

      isLoading.value = false; // end
      if (response.value && (val === "approve" || val === "reject")) {
        await nextTick();
        context.emit("advanced");
      }
    };

    // output encounter results
    const encounter = computed(() => {
      if (!response.value) {
        return null;
      } else {
        // billing insurance 
        // (if everything else fails, this wont!)
        // for pending encounters only
        response.value.claims.forEach((x) => {
          if (isProfessionalFeeClaim(x)) {
            x.approved_qty = 1;
            return;
          }

          if (x.status !== "d" && claimApprovalNeedsInitialization(x) && response.value.status === 'p') {
            initializeClaimApproval(x);
          }
        });
        syncProfessionalFeeApproval();

        return response.value;
      }
    });

    // check for empties
    watchEffect(() => {
      if (encounter.value) {
        if (!encounter.value?.claims?.length && encounter.value?.status === 'p') {
          // Next
          handleNext("approve");
        }
      }
    });

    return {
      capWords,
      hasClaimComment,
      hasClaimVettingAdjustment,
      handleAddClaimUpdate,
      handleAddClaim,
      handlePricing,
      handleEdit,
      handleReset,
      handleDelete,
      handleReVet,
      handleEncounterForDelete,
      handleEncounterDelete,
      handleClaimDelete,
      handleClaimEdit,
      handleRefresh,
      handleNext,
      handleBack,
      isProfessionalFeeClaim,
      isApprovedAuthorizationClaim,
      authorizationApprovalSlug,
      canEditClaim,
      canDeleteClaim,
      canResetClaim,
      totalSent,
      totalApproved,
      curr,
      total,
      deleteClaim,
      deleteHolder,
      editClaim,
      editHolder,
      deleteEncounter,
      isLoading,
      error,
      claimsCard,
      showLegacyExternalResolver,
      claimsDisplayCard,
      claimsSubmitCard,
      claimsList,
      item,
      qty,
      amt,
      standard,
      comment,
      reviewLimitMessage,
      adder,
      encounter,
      encounterAgeSex,
    };
  },
};
</script>

<style scoped>
.vetting-soft-core,
.vetting-soft-bio,
.vetting-soft-diagnosis,
.vetting-soft-claims,
.vetting-soft-edit,
.vetting-core-card,
.vetting-member-card,
.vetting-diagnosis-card,
.vetting-claims-card,
.vetting-edit-card,
.vetting-claim-item {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.vetting-soft-core {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  overflow: hidden;
}

.vetting-soft-core-add-open {
  overflow: visible;
}

.vetting-soft-bio {
  border-bottom: 1px solid #e1e8eb;
  margin: 0;
}

.vetting-soft-count {
  border-radius: 6px;
  padding-bottom: 8px !important;
  padding-top: 8px !important;
}

.vetting-soft-flag {
  border: 1px solid #ffe082;
  border-radius: 6px;
  margin-bottom: 0;
}

.vetting-soft-diagnosis {
  border-bottom: 1px solid #e1e8eb;
  margin-bottom: 0;
}

.vetting-soft-diagnosis .card-content {
  border-radius: 0;
  padding-bottom: 14px;
  padding-top: 14px;
}

.vetting-soft-claims {
  border: 0;
  box-shadow: none;
  margin: 12px 0 0;
}

.vetting-soft-add {
  border: 1px solid rgba(0, 137, 123, 0.18);
}

.vetting-soft-add-panel {
  padding: 16px 24px 0;
  position: relative;
  z-index: 10;
}

.vetting-soft-add-panel :deep(.card) {
  background: #ffffff !important;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  box-shadow: none !important;
  margin: 0;
  overflow: hidden;
  width: 100%;
}

.vetting-soft-add-panel :deep(.card.teal.lighten-5) {
  background-color: #ffffff !important;
}

.vetting-soft-add-panel :deep(.card.red.lighten-5) {
  background-color: #fff8f8 !important;
}

.vetting-soft-add-panel :deep(.card-content) {
  padding: 18px 22px 14px;
}

.vetting-soft-add-panel :deep(.row) {
  margin-bottom: 8px;
}

.vetting-soft-add-panel :deep(.input-field) {
  margin-bottom: 8px;
  margin-top: 8px;
}

.vetting-soft-add-panel :deep(.add-claim-item-card),
.vetting-soft-add-panel :deep(.add-claim-item-card .card-content),
.vetting-soft-add-panel :deep(.add-claim-item-card form),
.vetting-soft-add-panel :deep(.add-claim-item-card .row),
.vetting-soft-add-panel :deep(.add-claim-item-card .input-field) {
  overflow: visible !important;
}

.vetting-soft-add-panel :deep(.add-claim-item-card) {
  position: relative;
  z-index: 20;
}

.vetting-soft-add-panel :deep(.add-claim-item-card .autocomplete-content.dropdown-content) {
  max-height: 320px;
  overflow-y: auto;
  z-index: 1200;
}

.vetting-soft-add-panel :deep(.btn-large) {
  height: 40px;
  line-height: 40px;
  padding: 0 20px;
}

.vetting-soft-add-panel :deep(.btn-large i) {
  font-size: 1.25rem;
  line-height: 40px;
}

.vetting-soft-list {
  border-color: #e1e8eb;
  border-radius: 6px;
  overflow: hidden;
}

.vetting-soft-list .collection-item {
  border-bottom-color: #edf2f4;
  line-height: 1.48;
}

.vetting-soft-list .claim-line-item.has-inline-resolver {
  padding-right: 20px;
}

.vetting-soft-list .claim-line-item.is-inline-editing {
  border-left: 3px solid #00897b;
}

.vetting-soft-list .claim-line-item.is-inline-deleting {
  border-left: 3px solid #ef6c00;
}

.vetting-soft-list .claim-line-item {
  box-sizing: border-box;
  min-height: 54px;
  padding-right: 126px;
  position: relative;
}

.vetting-soft-list .claim-line-copy {
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
}

.vetting-soft-list .line-item-actions {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
}

.vetting-soft-list .line-item-actions .secondary-content {
  float: none;
  margin-left: 0;
  position: static;
}

.vetting-soft-list .line-item-actions a {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  position: relative;
  width: 30px;
}

.vetting-soft-list .line-item-actions a + a::before {
  background: #c4d3d9;
  content: "";
  height: 20px;
  left: -3px;
  position: absolute;
  top: 5px;
  width: 1px;
}

.vetting-soft-list .line-item-actions i {
  line-height: 1;
}

.vetting-soft-list .authorization-lock-inline {
  align-items: center;
  color: #607d8b;
  display: inline-flex;
  margin-left: 6px;
  position: relative;
  top: 2px;
  vertical-align: middle;
}

.vetting-soft-list .authorization-lock-inline i {
  font-size: 18px;
  line-height: 1;
}

.claim-item-resolver {
  background: #f8fbfb;
  border: 1px solid #d8e5e8;
  border-radius: 4px;
  box-sizing: border-box;
  display: grid;
  gap: 14px;
  margin-top: 14px;
  padding: 14px;
  width: 100%;
}

.claim-item-resolver.is-deleting {
  background: #fff8f2;
  border-color: #ffd5ad;
}

.claim-item-resolver.has-error {
  border-color: #ef9a9a;
}

.claim-item-resolver-heading {
  align-items: center;
  display: flex;
  gap: 10px;
}

.claim-item-resolver-heading > span:nth-child(2) {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.claim-item-resolver-heading strong {
  color: #263238;
  font-size: 0.92rem;
  line-height: 1.25;
}

.claim-item-resolver-heading small {
  color: #607d8b;
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 2px;
  overflow-wrap: anywhere;
}

.claim-item-resolver-icon,
.claim-item-resolver-dismiss {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.claim-item-resolver-icon {
  background: #e4f3f1;
  border: 1px solid #b9ddd8;
  border-radius: 4px;
  color: #00796b;
}

.claim-item-resolver.is-deleting .claim-item-resolver-icon {
  background: #fff0e3;
  border-color: #ffc58f;
  color: #d95f00;
}

.claim-item-resolver-icon i,
.claim-item-resolver-dismiss i {
  font-size: 19px;
  line-height: 1;
}

.claim-item-resolver-dismiss {
  background: #ffffff;
  border: 1px solid #cfdde2;
  border-radius: 4px;
  color: #607d8b;
  cursor: pointer;
  margin-left: auto;
  padding: 0;
}

.claim-item-resolver-dismiss:hover,
.claim-item-resolver-dismiss:focus {
  background: #fff3e0;
  border-color: #ffb74d;
  color: #e65100;
  outline: none;
}

.claim-item-resolver-fields {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.claim-item-resolver-fields.is-professional-fee p {
  color: #607d8b;
  font-size: 0.8rem;
  font-weight: 700;
  grid-column: 1 / -1;
  margin: 0;
}

.claim-item-resolver label {
  color: #546e7a;
  display: grid;
  font-size: 0.76rem;
  font-weight: 700;
  gap: 5px;
  letter-spacing: 0;
}

.claim-item-resolver input[type="number"],
.claim-item-resolver input[type="text"] {
  background: #ffffff;
  border: 1px solid #cfdde2 !important;
  border-radius: 4px;
  box-shadow: none !important;
  box-sizing: border-box;
  color: #263238;
  font-size: 0.9rem;
  height: 40px;
  margin: 0;
  padding: 0 11px;
  width: 100%;
}

.claim-item-resolver input[readonly],
.claim-item-resolver input:disabled {
  background: #f1f5f6;
  color: #455a64;
}

.claim-item-resolver input:focus {
  border-color: #00897b !important;
}

.claim-item-resolver.is-deleting input:focus {
  border-color: #ef6c00 !important;
}

.claim-item-resolver .authorization-review-warning,
.claim-item-resolver .review-limit-message {
  margin: 0;
}

.claim-item-resolver-actions {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.claim-item-resolver-actions button {
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  justify-content: center;
  letter-spacing: 0;
  min-height: 36px;
  padding: 7px 12px;
}

.claim-item-resolver-cancel {
  background: #ffffff;
  border: 1px solid #cfdde2;
  color: #546e7a;
}

.claim-item-resolver-submit {
  background: #00796b;
  border: 1px solid #00796b;
  color: #ffffff;
}

.claim-item-resolver-submit.is-danger {
  background: #ffffff;
  border-color: #ef6c00;
  color: #d95f00;
}

.claim-item-resolver-actions button:hover,
.claim-item-resolver-actions button:focus {
  filter: brightness(0.97);
  outline: none;
}

.claim-item-resolver-actions i {
  font-size: 17px;
  line-height: 1;
}

.professional-fee-badge {
  background: #e8f4f2;
  border: 1px solid #b7d9d4;
  border-radius: 999px;
  color: #00796b;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 1;
  margin-left: 10px;
  padding: 6px 12px;
  text-transform: uppercase;
  vertical-align: middle;
}

.professional-fee-edit-panel {
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  padding: 4px 10px 0;
}

.professional-fee-amount-field {
  background: #f7fbfa;
  border: 1px solid #cfe1df;
  border-radius: 5px;
  margin-top: 8px;
  padding: 10px 12px 2px !important;
}

.professional-fee-amount-field label {
  font-weight: 800;
  left: 12px !important;
}

.professional-fee-amount-field input {
  border-bottom-color: #80cbc4 !important;
  box-sizing: border-box !important;
  color: #263238;
  font-size: 1.05rem !important;
  font-weight: 800;
  margin-bottom: 4px !important;
}

.professional-fee-edit-note {
  color: #607d8b;
  font-size: 0.86rem;
  font-weight: 700;
  margin: -4px 0 12px;
}

.authorization-review-warning {
  align-items: center;
  background: #fffdf6;
  border: 1px solid #f5d28c;
  border-radius: 5px;
  color: #37474f;
  display: flex;
  font-size: 0.92rem;
  font-weight: 600;
  gap: 10px;
  line-height: 1.5;
  margin: 0 0 18px;
  padding: 12px 14px;
}

.authorization-review-warning i {
  color: #f57c00;
  flex: 0 0 auto;
  font-size: 22px;
}

.review-limit-message {
  background: #fff8f8;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  color: #b71c1c;
  font-weight: 700;
  margin: 0 0 16px;
  padding: 10px 12px;
}

.vetting-soft-list .collection-item:nth-child(even) {
  background: #fbfcfc;
}

.vetting-soft-total {
  border-color: #d9e1e5;
  border-radius: 6px;
  overflow: hidden;
}

.vetting-soft-total .collection-item {
  background: #f8fafb;
  border-bottom: 0;
}

.vetting-soft-actions {
  margin-top: 24px;
  margin-bottom: 0;
}

.vetting-soft-back-action {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  box-shadow: none;
  color: #455a64;
  margin-left: 10px;
}

.vetting-soft-back-action:hover,
.vetting-soft-back-action:focus {
  background: #f8fafb;
  box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
  color: #263238;
}

.vetting-soft-close-action {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  box-shadow: 0 6px 14px -12px rgba(38, 50, 56, 0.75);
  color: #455a64;
}

.vetting-soft-close-action:hover,
.vetting-soft-close-action:focus {
  background: #f8fafb;
  box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
  color: #263238;
}

.vetting-soft-close-action i {
  color: inherit;
}

.vetting-soft-next-action {
  margin-left: 10px;
}

.vetting-soft-edit {
  border: 1px solid #d9e1e5;
  border-radius: 6px;
}

.vetting-core-card {
  background: #ffffff;
  border: 1px solid #d9e1e5;
  border-radius: 6px;
  box-shadow: 0 12px 22px -24px rgba(38, 50, 56, 0.65);
  overflow: hidden;
}

.vetting-member-card {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e1e8eb;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 22px 24px;
}

.vetting-member-card.is-deleted {
  background: #fff7f7;
}

.vetting-member-main {
  align-items: center;
  display: flex;
  gap: 16px;
  min-width: 0;
}

.vetting-step-chip {
  align-items: baseline;
  background: #f8fafb;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  color: #263238;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 900;
  gap: 2px;
  min-width: 54px;
  padding: 8px 10px;
}

.vetting-step-chip span {
  font-size: 1.05rem;
}

.vetting-step-chip small {
  color: #78909c;
  font-weight: 800;
}

.vetting-member-copy {
  min-width: 0;
}

.vetting-member-copy > p {
  color: #607d8b;
  font-size: 0.82rem;
  font-weight: 700;
  margin: 0 0 4px;
}

.vetting-member-copy h5 {
  color: #263238;
  font-size: 1.38rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.vetting-enrollee-number {
  color: #455a64;
  display: inline-flex;
  font-size: 0.88rem;
  font-weight: 700;
  margin-top: 5px;
}

.vetting-date-line {
  align-items: center;
  color: #455a64;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.84rem;
  font-weight: 700;
  gap: 7px;
  margin: 8px 0 0;
}

.vetting-date-line i {
  color: #90a4ae;
  font-size: 16px;
}

.vetting-date-line.is-muted {
  color: #78909c;
}

.vetting-review-markers {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 10px;
  max-width: 720px;
}

.vetting-review-marker {
  align-items: flex-start;
  border: 1px solid #c4d3d9;
  border-radius: 8px;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 8px;
  line-height: 1.45;
  padding: 8px 10px;
}

.vetting-review-marker i {
  flex: 0 0 auto;
  font-size: 17px;
  line-height: 1.35;
}

.vetting-review-marker.is-duplicate {
  background: #fff8e8;
  border-color: #e7b65f;
  color: #6f4b00;
}

.vetting-review-marker.is-multiple {
  background: #f1f4f6;
  border-color: #b0bec5;
  color: #37474f;
}

.vetting-member-photo img {
  border: 3px solid #ffffff;
  box-shadow: 0 9px 18px -16px rgba(38, 50, 56, 0.8);
  display: block;
  height: 74px;
  object-fit: cover;
  width: 74px;
}

.vetting-flag-note {
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 6px;
  color: #5d4037;
  flex: 0 0 250px;
  padding: 10px 12px;
}

.vetting-flag-note label {
  align-items: center;
  color: #bf7200;
  display: flex;
  font-weight: 900;
  gap: 5px;
  text-transform: uppercase;
}

.vetting-flag-note p {
  margin: 4px 0 0;
}

.vetting-diagnosis-card {
  align-items: center;
  background: #f8fafb;
  border-bottom: 1px solid #e1e8eb;
  display: flex;
  gap: 12px;
  padding: 15px 24px;
}

.vetting-diagnosis-icon {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d9e1e5;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.vetting-diagnosis-icon i {
  font-size: 19px;
}

.vetting-diagnosis-card span {
  color: #607d8b;
  display: block;
  font-size: 0.76rem;
  font-weight: 900;
  text-transform: uppercase;
}

.vetting-diagnosis-card p {
  color: #263238;
  font-weight: 800;
  line-height: 1.35;
  margin: 2px 0 0;
}

.vetting-claims-card {
  background: #ffffff;
}

.vetting-claims-content {
  padding: 20px 24px 22px;
}

.vetting-claims-title {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
}

.vetting-claims-title span {
  color: #263238;
  display: block;
  font-size: 1.05rem;
  font-weight: 900;
}

.vetting-claims-title small {
  color: #78909c;
  display: block;
  font-weight: 700;
  margin-top: 2px;
}

.vetting-add-claim-action {
  align-items: center;
  background: #e0f2f1;
  border: 1px solid #80cbc4;
  border-radius: 50%;
  color: #00695c;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.vetting-add-claim-action.is-open {
  background: #fff3e0;
  border-color: #ffcc80;
  color: #e65100;
}

.vetting-add-claim-action i {
  font-size: 20px;
}

.vetting-claim-list {
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  counter-reset: vetting-claims;
  margin: 0;
  overflow: hidden;
  padding: 0;
}

.vetting-claim-item {
  background: #ffffff;
  border-bottom: 1px solid #edf2f4;
  color: #37474f;
  counter-increment: vetting-claims;
  line-height: 1.55;
  list-style: none;
  min-height: 48px;
  padding: 13px 92px 13px 48px;
  position: relative;
}

.vetting-claim-item::before {
  color: #90a4ae;
  content: counter(vetting-claims, decimal-leading-zero);
  font-size: 0.72rem;
  font-weight: 900;
  left: 16px;
  position: absolute;
  top: 16px;
}

.vetting-claim-item:nth-child(even) {
  background: #fbfcfc;
}

.vetting-claim-item:last-child {
  border-bottom: 0;
}

.vetting-claim-item.is-adjusted {
  background: #fffaf0;
}

.vetting-claim-item.is-deleted {
  background: #f7f8f9;
  color: #78909c;
}

.vetting-claim-item.is-locked {
  border-left: 3px solid #90a4ae;
}

.vetting-claim-item :deep(.secondary-content),
.vetting-claim-item .secondary-content {
  margin-left: 8px;
}

.vetting-total-bar {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #d9e1e5;
  border-radius: 6px;
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
  padding: 12px 14px;
}

.vetting-total-bar.is-legacy-total {
  list-style: none;
}

.vetting-total-bar li {
  list-style: none;
  width: 100%;
}

.vetting-total-bar .right {
  color: #455a64;
  display: block;
  float: none !important;
  font-weight: 700;
  text-align: right;
}

.vetting-action-row {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #d9e1e5;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-top: 24px;
  padding: 12px;
}

.vetting-action-row.is-complete {
  justify-content: flex-end;
}

.vetting-action-group {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.vetting-action-btn {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 4px;
  box-shadow: 0 10px 20px -18px rgba(38, 50, 56, 0.8);
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  letter-spacing: 0;
  min-height: 44px;
  min-width: 124px;
  padding: 0 18px;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.vetting-action-btn i {
  color: currentColor;
  font-size: 19px;
  line-height: 1;
}

.vetting-action-btn.is-danger {
  background: #fff7f7;
  border-color: #ef9a9a;
  color: #c62828;
}

.vetting-action-btn.is-danger:hover,
.vetting-action-btn.is-danger:focus-visible {
  background: #ffebee;
  border-color: #e57373;
  color: #b71c1c;
}

.vetting-action-btn.is-secondary {
  background: #ffffff;
  border-color: #c4d3d9;
  color: #455a64;
}

.vetting-action-btn.is-secondary:hover,
.vetting-action-btn.is-secondary:focus-visible {
  background: #eef3f5;
  border-color: #90a4ae;
  color: #263238;
}

.vetting-action-btn.is-warning {
  background: #fff8e1;
  border-color: #ffcc80;
  color: #e65100;
}

.vetting-action-btn.is-warning:hover,
.vetting-action-btn.is-warning:focus-visible {
  background: #ffecb3;
  border-color: #ffb74d;
  color: #bf360c;
}

.vetting-action-btn.is-primary {
  background: #00897b;
  border-color: #00897b;
  color: #ffffff;
  min-width: 156px;
}

.vetting-action-btn.is-primary:hover,
.vetting-action-btn.is-primary:focus-visible {
  background: #00796b;
  border-color: #00796b;
  box-shadow: 0 14px 24px -20px rgba(38, 50, 56, 0.85);
  color: #ffffff;
  transform: translateY(-1px);
}

.vetting-action-btn.is-primary.is-approve {
  background: #00796b;
  border-color: #00695c;
}

.vetting-action-btn.disabled {
  box-shadow: none;
  cursor: default;
  opacity: 0.45;
  pointer-events: none;
}

.vetting-edit-card {
  background: #f8fafb;
  border: 1px solid #d9e1e5;
  border-radius: 6px;
  margin: 14px 24px 24px;
}

.vetting-edit-card.has-error {
  background: #fff7f7;
  border-color: #ffcdd2;
}

.vetting-edit-card .card-content {
  padding: 20px;
}

.spacer10 {
  margin-top: 10px;
  margin-bottom: 10px;
  margin-right: 10px;
  margin-left: 10px;
}

.custom-hr {
  width: 50%;
  /* Adjust the width as needed */
  border: none;
  border-top: 2px solid #ccc;
  /* Adjust the color and style as needed */
  margin: 20px 20px;
  /* Adjust margin as needed */
}

.counter {
  display: block;
  padding: 0.5em;
  /* Adjust padding to your liking */
  /* font-size: 1em;  Adjust font size */
}

.clickable {
  cursor: pointer;
  opacity: 1;
  /* Default opacity */
  transition: opacity 0.3s ease;
  /* Smooth opacity change */
}

.clickable:hover {
  opacity: 0.8;
  /* Slightly reduced opacity on hover */
}

.pointable {
  cursor: default;
  /* opacity: 1; Default opacity */
  /* transition: opacity 0.3s ease;  Smooth opacity change */
}

/* .pointable:hover {
    opacity: 0.8; Slightly reduced opacity on hover
  } */

@media only screen and (max-width: 992px) {
  .vetting-soft-list .claim-line-item {
    padding-right: 20px;
  }

  .vetting-soft-list .line-item-actions {
    background: #f8fafb;
    border: 1px solid #dbe5e9;
    border-radius: 999px;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    gap: 10px;
    margin-left: 10px;
    margin-top: 10px;
    padding: 4px;
    position: static;
    transform: none;
    width: max-content;
  }

  .vetting-soft-list .claim-line-copy .info-item:not(.secondary-content):not(.authorization-lock-inline) {
    display: inline-flex;
    margin-right: 10px;
    position: relative;
    top: -0.35em;
    vertical-align: super;
  }

  .vetting-soft-list .claim-line-copy .info-item:not(.secondary-content):not(.authorization-lock-inline) i {
    line-height: 1;
  }

  .vetting-soft-list .line-item-actions a {
    height: 34px;
    width: 34px;
  }

  .vetting-soft-list .line-item-actions a + a::before {
    display: none;
  }

  .vetting-soft-list .line-item-actions .tiny.material-icons {
    font-size: 1.25rem;
  }
}

@media only screen and (max-width: 700px) {
  .claim-item-resolver-fields {
    grid-template-columns: 1fr;
  }

  .claim-item-resolver-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .claim-item-resolver-actions button {
    width: 100%;
  }

  .vetting-member-card,
  .vetting-member-main,
  .vetting-claims-title,
  .vetting-action-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .vetting-member-card {
    padding: 18px;
  }

  .vetting-member-photo img {
    height: 64px;
    width: 64px;
  }

  .vetting-flag-note {
    flex-basis: auto;
    width: 100%;
  }

  .vetting-diagnosis-card,
  .vetting-claims-content {
    padding-left: 18px;
    padding-right: 18px;
  }

  .vetting-claim-item {
    padding-right: 16px;
  }

  .vetting-action-btn,
  .vetting-action-btn.is-primary {
    justify-content: center;
    margin-left: 0;
    width: 100%;
  }

  .vetting-action-group {
    align-items: stretch;
    flex-direction: column;
    margin-left: 0;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .vetting-soft-core,
  .vetting-soft-bio,
  .vetting-soft-diagnosis,
  .vetting-soft-claims,
  .vetting-soft-edit,
  .vetting-core-card,
  .vetting-member-card,
  .vetting-diagnosis-card,
  .vetting-claims-card,
  .vetting-edit-card,
  .vetting-claim-item {
    animation: none;
  }
}
</style>
